import useTwinTranslation from '../../../../../utils/hooks/useTwinTranslation'
import { TwinDictionary } from '../../../../../utils/globals/dictionary'
import { getQuoteOrderStatusBarType } from './functions'
import { QuoteOrderLogModelType } from '@teinor/erp/types/company/order/quote/quoteOrder/quoteOrderLog'
import OrderStatusBar from '../../OrderLayout/OrderStatusBar'
import { displayDate } from '../../../../../utils/globals/date'
import { Fragment } from 'react'
import { QuoteOrderStatusInversed } from '@teinor/erp/types/company/order/quote/quoteOrder'
import OrderStatusBarDropDown from '../../OrderLayout/OrderStatusBarDropDown'
import { faClock, faFileCheck, faPenCircle, faShare } from '@fortawesome/pro-light-svg-icons'
import { faCheckCircle, faXmarkCircle } from '@fortawesome/pro-solid-svg-icons'

export interface QuoteOrderStatusBarProps extends CreateStatusBarProps {}

const QuoteOrderStatusBar: React.FC<QuoteOrderStatusBarProps> = ({ quoteOrderStatus, isLast, versionsDict, OrderLogs, isVersion=false, customerName }) => {
    const { statuses } = useCreateStatusBarLogic({ quoteOrderStatus, isVersion, OrderLogs, versionsDict, isLast, customerName })
    
    return (
        <OrderStatusBar statuses={statuses} currentOrderStatus={quoteOrderStatus} getOrderStatusBarType={getQuoteOrderStatusBarType} isCanceled={quoteOrderStatus === QuoteOrderStatusInversed['denied'] ? true: false} />
    )
}

interface CreateStatusBarProps {
    isVersion?: boolean
    quoteOrderStatus: number
    OrderLogs: QuoteOrderLogModelType[]
    versionsDict: TwinDictionary
    isLast: boolean
    customerName: string
}

const useCreateStatusBarLogic = ({ quoteOrderStatus, isVersion, OrderLogs, versionsDict, isLast, customerName }: CreateStatusBarProps) => {
    const { t } = useTwinTranslation()
    let statuses: TwinDictionary[] = []
    const dictOrderLogs: TwinDictionary = {}
    for (const log of OrderLogs) {
        let myLogType = log.logType
        const myTransforms: TwinDictionary = {
            5: 1,
            6: 3,
            7: 4,
        }
        if (myTransforms[myLogType]) {
            myLogType = myTransforms[myLogType]
        }
        if (!dictOrderLogs[myLogType]) {
            dictOrderLogs[myLogType] = [log]
        } else {
            dictOrderLogs[myLogType] = [...dictOrderLogs[myLogType], log]
        }
    }
    if (isVersion) {
        statuses.push(
            {
                id: -1,
                name: t('draft', 'Borrador'),
                logType: 0,
                color: 'bg-gray-33',
                icon: faPenCircle,
                className: 'text-gray-33',
                extraClassName: 'border border-gray-33',
                renderMessage: dictOrderLogs[0]?.length ? <RenderLogMessage logs={dictOrderLogs[0] || []} versionsDict={versionsDict} {...{ customerName }} /> : null
            },
            {
                id: 0,
                name: quoteOrderStatus === QuoteOrderStatusInversed['creating'] ? t('modifying', 'Modificando') : t('modified', 'Modificado'),
                logType: 1,
                color: 'bg-blue-05',
                icon: faPenCircle,
                className: 'text-blue-05',
                extraClassName: 'border border-blue-05', 
                renderMessage: dictOrderLogs[1]?.length ? <RenderLogMessage logs={dictOrderLogs[1] || []} versionsDict={versionsDict} {...{ customerName }} /> : null
            }
        )
    } else {
        statuses.push({
            id: 0,
            name: t('draft', 'Borrador'),
            logType: 0,
            color: 'bg-gray-33',
            icon: faPenCircle,
            className: 'text-gray-33',
            extraClassName: 'border border-gray-33',
            renderMessage: dictOrderLogs[0]?.length ? <RenderLogMessage logs={dictOrderLogs[0] || []} versionsDict={versionsDict} {...{ customerName }} /> : null
        })
    }
    statuses.push(
        {
            id: 1,
            name: t('pending', 'Pendiente'), logType: 2,
            color: 'bg-orange-DE',
            icon: faClock,
            className: 'text-orange-DE',
            extraClassName: 'border border-orange-DE',
            renderMessage: dictOrderLogs[2]?.length ? <RenderLogMessage logs={dictOrderLogs[2] || []} versionsDict={versionsDict} {...{ customerName }} /> : null
        }
    )
    if (dictOrderLogs[8]?.length) {
        statuses.push(
            {
                id: 2,
                name: t('OrderSent', 'Presupuesto enviado'), logType: 8,
                color: 'bg-purple-86',
                icon: faShare,
                className: 'text-purple-86',
                extraClassName: 'border border-purple-86',
                renderMessage: dictOrderLogs[8]?.length ? <RenderLogMessage logs={dictOrderLogs[8] || []} versionsDict={versionsDict} {...{ customerName }} /> : null
            }
        )
    }
    if (quoteOrderStatus === QuoteOrderStatusInversed['denied']) {
        statuses.push({
            id: 4,
            name: t('deniedP', 'Cancelado'),
            logType: 4,
            color: 'bg-red-BA',
            icon: faXmarkCircle,
            className: 'text-red-BA',
            renderMessage: dictOrderLogs[4]?.length ? <RenderLogMessage logs={dictOrderLogs[4] || []} versionsDict={versionsDict} {...{ customerName }} /> : null
        })
    } else {
        statuses.push({
            id: 3,
            name: t('accepted', 'Aceptado'),
            logType: 3,
            color: 'bg-green-43',
            icon: faCheckCircle,
            className: 'text-green-43',
            renderMessage: dictOrderLogs[3]?.length ? <RenderLogMessage logs={dictOrderLogs[3] || []} versionsDict={versionsDict} {...{ customerName }} /> : null
        })
    }
    if (quoteOrderStatus === QuoteOrderStatusInversed['processed']) {
        statuses.push({
            id: 5,
            name: t('processed', 'Procesado'),
            logType: 9,
            color: 'bg-green-43',
            icon: faFileCheck,
            className: 'text-green-43',
            extraClassName: 'border border-green-43',
            renderMessage: dictOrderLogs[9]?.length ? <RenderLogMessage logs={dictOrderLogs[9] || []} versionsDict={versionsDict} {...{ customerName }} /> : null
        })
    }

    if (!isLast) {
        statuses = [{
            id: 6, name: t('oldVersion', 'Versión antigua'),
            renderMessage: null
        }]
    }
    return { statuses }
}

interface RenderLogMessageProps {
    logs: QuoteOrderLogModelType[]
    versionsDict: TwinDictionary
    customerName: string
}

const RenderLogMessage: React.FC<RenderLogMessageProps> = ({ logs, versionsDict, customerName }) => {
    const { tVars } = useTwinTranslation()
    const renderMessage: JSX.Element[] = []
    for (const log of logs) {
        const myExtraData = log?.extraData ? JSON.parse(log?.extraData) : null
        const options = { employeeTrigger: log?.EmployeeTrigger?.fullname_short || '', version: versionsDict[log.OrderId], date: displayDate(new Date(log.createdAt)), customerName, fullAssignedNumber: myExtraData?.full_assigned_number || '', SellOrderId: myExtraData?.SellOrderId, SellDeliveryNoteId: myExtraData?.SellDeliveryNoteId, SellInvoiceId: myExtraData?.SellInvoiceId, 'interpolation': { 'escapeValue': false }, }
        const dictOrderLogsMessages: TwinDictionary = {
            0: tVars('createdOrderLogMessage', '{{employeeTrigger}} creó el presupuesto el {{date}}', options),
            1: tVars('createdVersionOrderLogMessage', '{{employeeTrigger}} creó la versión {{version}} el {{date}}', options),
            2: tVars('waitingResponseOrderLogMessage', '{{employeeTrigger}} marcó la versión {{version}} como esperando respuesta el {{date}}', options),
            3: tVars('acceptedOrderLogMessage', '{{employeeTrigger}} marcó el presupuesto como aceptado el {{date}}', options),
            4: tVars('deniedOrderLogMessage', '{{employeeTrigger}} marcó el presupuesto como denegado el {{date}}', options),
            5: tVars('createdVersionOrderLogMessageCustomer', '{{customerName}} creó la versión {{version}} el {{date}}', options),
            6: tVars('acceptedOrderLogMessageCustomer', '{{customerName}} marcó el presupuesto como aceptado el {{date}}', options),
            7: tVars('deniedOrderLogMessageCustomer', '{{customerName}} marcó el presupuesto como denegado el {{date}}', options),
            8: tVars('sentOrderLogMessage', '{{employeeTrigger}} envió el presupuesto el {{date}}', options),
            9: myExtraData?.SellOrderId ?
                tVars('processedSellOrderOrderLogMessage', '{{employeeTrigger}} creó el pedido {{fullAssignedNumber}} el {{date}}', options)
                : myExtraData?.SellDeliveryNoteId ? 
                    tVars('processedSellDeliveryNoteOrderLogMessage', '{{employeeTrigger}} creó el albarán {{fullAssignedNumber}} el {{date}}', options)
                    : myExtraData?.SellInvoiceId ?
                        tVars('processedSellInvoiceOrderLogMessage', '{{employeeTrigger}} creó la factura {{fullAssignedNumber}} el {{date}}', options): '',
        }
        if (dictOrderLogsMessages[log.logType]) {
            renderMessage.push(<div className='my-5' key={log.id} > {dictOrderLogsMessages[log.logType]} </div>)
        }
    }
    if (!renderMessage.length) {
        return null
    }
    return (<Fragment>{renderMessage}</Fragment>)
}

export const QuoteOrderStatusBarNew: React.FC<QuoteOrderStatusBarProps> = ({ quoteOrderStatus, isLast, versionsDict, OrderLogs, isVersion = false, customerName }) => {
    const { statuses } = useCreateStatusBarLogic({ quoteOrderStatus, isVersion, OrderLogs, versionsDict, isLast, customerName })
    return (
        <OrderStatusBarDropDown statuses={statuses} currentOrderStatus={quoteOrderStatus} getOrderStatusBarType={getQuoteOrderStatusBarType} isCanceled={quoteOrderStatus === QuoteOrderStatusInversed['denied'] ? true : false} isAccepted={quoteOrderStatus === QuoteOrderStatusInversed['accepted'] || quoteOrderStatus === QuoteOrderStatusInversed['processed']} />
    )
}

export default QuoteOrderStatusBar